<template>
  <div class="lesson-video-player">
    <transition name="fade" v-if="!isHotmart">
      <div v-if="!playing" class="lesson-name">{{ lesson.name }}</div>
    </transition>

    <video
      controls
      playsinline
      crossorigin
      v-if="isStream"
      :id="id"
      :poster="poster"
      class="w-100"
    ></video>

    <div
      v-else-if="lesson.vimeo_url || lesson.youtube_url"
      class="embed-responsive embed-responsive-16by9"
      :id="id"
    >
      <iframe class="embed-responsive-item" :src="src" allowfullscreen></iframe>
    </div>

    <div v-else-if="src" class="embed-responsive embed-responsive-16by9">
      <iframe class="embed-responsive-item" :src="src" allowfullscreen></iframe>
    </div>
  </div>
</template>

<script>
import Hls from "hls.js";
import Plyr from "plyr";
import get from "lodash/get";
import isMobile from "ismobilejs";

export default {
  props: {
    lesson: {
      type: Object,
      default: () => ({})
    },
    progress: {
      type: Object,
      default: () => ({})
    },
    id: {
      type: String,
      default: "lesson-video-player"
    }
  },
  data() {
    return {
      player: null,
      playing: false,
      videoElement: null,
      playerOptions: {
        autoplay: false,
        invertTime: false,
        i18n: {
          speed: "Velocidade",
          quality: "Qualidade",
          qualityLabel: {
            0: "Auto"
          }
        },
        vimeo: {
          playsinline: false
        },
        youtube: {
          playsinline: false
        },
        speed: {
          selected: 1,
          options: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2, 3]
        }
      }
    };
  },
  computed: {
    poster() {
      return this.lesson.file || this.lesson.thumbnail_vimeo;
    },
    isStream() {
      return (
        (this.lesson.link || "").endsWith(".m3u8") ||
        (!this.lesson.youtube_url && this.lesson.stream_url)
      );
    },
    isHotmart() {
      return this.lesson.link.includes("play.hotmart");
    },
    src() {
      if (this.lesson.vimeo_url) {
        return this.lesson.vimeo_url;
      }
      if (this.lesson.youtube_url) {
        return this.lesson.youtube_url;
      }
      return this.lesson.link;
    },
    streamUrl() {
      return this.lesson.stream_url || this.lesson.link;
    }
  },
  mounted() {
    if (this.lesson.is_iframe) return;

    this.videoElement = document.getElementById(this.id);

    if (this.isStream) {
      if (this.videoElement.canPlayType("application/vnd.apple.mpegURL")) {
        let source = document.createElement("source");
        source.src = this.streamUrl;
        source.type = "application/vnd.apple.mpegURL";
        this.videoElement.appendChild(source);
      } else if (Hls.isSupported()) {
        var config = {
          autoStartLoad: false
        };

        const hls = new Hls(config);
        hls.loadSource(this.streamUrl);
        hls.on(Hls.Events.MANIFEST_PARSED, function() {
          hls.startLoad();
        });

        hls.on(Hls.Events.MANIFEST_PARSED, () => {
          const availableQualities = hls.levels.map(l => l.height).reverse();
          this.playerOptions.quality = {
            default: 0,
            options: [0, ...availableQualities],
            forced: true,
            onChange: e => updateQuality(e)
          };
          this.initPlayer();
        });

        const updateQuality = newQuality => {
          if (newQuality === 0) {
            hls.currentLevel = -1;
          } else {
            hls.levels.forEach((level, levelIndex) => {
              if (level.height === newQuality) {
                hls.currentLevel = levelIndex;
              }
            });
          }
        };

        hls.attachMedia(this.videoElement);
      }
    } else {
      this.initPlayer();
    }
  },
  destroyed() {
    clearInterval(this.interval);
  },
  methods: {
    initPlayer() {
      let controls = [
        "play-large",
        "play",
        "progress",
        "current-time",
        "mute",
        "volume",
        "captions",
        "settings",
        "pip",
        "airplay"
      ];

      if (!isMobile(window.navigator).any) {
        controls = ["rewind", "fast-forward", ...controls, "fullscreen"];
      }

      this.playerOptions.controls = controls;

      this.player = new Plyr(this.videoElement, this.playerOptions);

      this.player.on("play", () => {
        this.playing = true;
      });

      this.player.on("pause", () => {
        this.playing = false;
      });

      var firstPlay = true;

      this.player.on("play", () => {
        if (firstPlay) {
          setTimeout(() => {
            this.player.currentTime = get(this.progress, "stopped_at", 0);
          }, 1);
          firstPlay = false;
        }
      });

      const saveCurrentPosition = () => {
        this.saveDuration({
          finished: this.player.currentTime === this.duration,
          duration: this.player.currentTime
        });
      };

      this.interval = setInterval(() => {
        if (this.player.playing) {
          saveCurrentPosition();
        }
      }, 20 * 1000);

      this.player.on("pause", saveCurrentPosition);

      this.player.on("ended", event => {
        setTimeout(() => {
          this.saveDuration({ finished: true, duration: 0 });
        }, 200);
      });
    },
    saveDuration({ finished, duration }) {
      this.$store.dispatch("students/setProgress", {
        resource_id: this.lesson.id,
        stopped_at: parseInt(duration),
        finished_at: finished
      });
    }
  }
};
</script>

<style lang="scss" scoped>
.lesson-video-player {
  position: relative;

  .lesson-name {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    padding: 8px 12px;
    background: linear-gradient(rgba(0, 0, 0, 0.9), rgba(0, 0, 0, 0));
    color: white;
    font-weight: bold;
    font-size: 16px;
    text-shadow: 0 0 8px black;
    z-index: 11;
  }

  .btn-link {
    opacity: 0.7;
    font-size: 10px;
  }
}
</style>
